import {
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  ChartBarIcon,
  CurrencyDollarIcon,
  ShoppingCartIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';
import { FC } from 'react';
import styled from 'styled-components';

const AnalyticsContainer = styled.div`
  min-height: 100vh;
  background: var(--gray-50);
`;

const AnalyticsContent = styled.div`
  padding: 2.5rem 0;
`;

const Header = styled.header`
  max-width: 1560px;
  margin: 0 auto;
  padding: 0 2rem;
  margin-bottom: 2rem;
`;

const HeaderTop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  flex-wrap: wrap;
  gap: 1rem;
`;

const Title = styled.h1`
  font-size: 1.875rem;
  font-weight: 700;
  color: var(--gray-900);
`;

const DateRangePicker = styled.select`
  padding: 0.5rem 2.5rem 0.5rem 1rem;
  border: 1px solid var(--gray-300);
  border-radius: 0.5rem;
  font-size: 0.875rem;
  background: white;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: var(--primary-600);
    box-shadow: 0 0 0 3px rgba(234, 88, 12, 0.1);
  }
`;

const Main = styled.main`
  max-width: 1560px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const MetricsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
`;

const MetricCard = styled.div`
  background: white;
  border-radius: 0.75rem;
  padding: 1.5rem;
  box-shadow: var(--shadow-sm);
  transition: all 0.2s;

  &:hover {
    box-shadow: var(--shadow-md);
    transform: translateY(-2px);
  }
`;

const MetricHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
`;

const MetricIcon = styled.div<{ $color: string; $bg: string }>`
  width: 48px;
  height: 48px;
  background: ${(props) => props.$bg};
  color: ${(props) => props.$color};
  border-radius: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 24px;
    height: 24px;
  }
`;

const MetricLabel = styled.div`
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--gray-600);
  margin-bottom: 0.5rem;
`;

const MetricValue = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: var(--gray-900);
  margin-bottom: 0.5rem;
`;

const MetricChange = styled.div<{ $positive: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: ${(props) =>
    props.$positive ? 'var(--success-600)' : 'var(--error-600)'};

  svg {
    width: 1rem;
    height: 1rem;
  }
`;

const ChartsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;
  margin-bottom: 2rem;

  @media (min-width: 1024px) {
    grid-template-columns: 2fr 1fr;
  }
`;

const ChartCard = styled.div`
  background: white;
  border-radius: 0.75rem;
  padding: 1.5rem;
  box-shadow: var(--shadow-sm);
`;

const ChartHeader = styled.div`
  margin-bottom: 1.5rem;

  h3 {
    font-size: 1.125rem;
    font-weight: 600;
    color: var(--gray-900);
    margin-bottom: 0.25rem;
  }

  p {
    font-size: 0.875rem;
    color: var(--gray-600);
  }
`;

const ChartPlaceholder = styled.div`
  height: 300px;
  background: linear-gradient(135deg, var(--gray-50) 0%, var(--gray-100) 100%);
  border-radius: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--gray-400);
  font-size: 0.875rem;
  border: 2px dashed var(--gray-300);
`;

const TopProductsTable = styled.table`
  width: 100%;
  border-collapse: collapse;

  thead {
    th {
      text-align: left;
      padding: 0.75rem 0.5rem;
      font-size: 0.75rem;
      font-weight: 600;
      color: var(--gray-600);
      text-transform: uppercase;
      letter-spacing: 0.05em;
      border-bottom: 1px solid var(--gray-200);
    }
  }

  tbody {
    tr {
      border-bottom: 1px solid var(--gray-100);

      &:last-child {
        border-bottom: none;
      }

      &:hover {
        background: var(--gray-50);
      }
    }

    td {
      padding: 1rem 0.5rem;
      font-size: 0.875rem;
      color: var(--gray-700);
    }
  }
`;

const ProductName = styled.div`
  font-weight: 500;
  color: var(--gray-900);
  margin-bottom: 0.25rem;
`;

const ProductSKU = styled.div`
  font-size: 0.75rem;
  color: var(--gray-500);
`;

const SalesValue = styled.div`
  font-weight: 600;
  color: var(--gray-900);
`;

const SalesCount = styled.div`
  font-size: 0.75rem;
  color: var(--gray-500);
`;

const FullWidthCard = styled(ChartCard)`
  @media (min-width: 1024px) {
    grid-column: 1 / -1;
  }
`;

const Analytics: FC = () => {
  const metrics = [
    {
      label: 'Total Revenue',
      value: '$45,231',
      change: '+20.1%',
      positive: true,
      icon: CurrencyDollarIcon,
      color: '#10b981',
      bg: '#d1fae5',
    },
    {
      label: 'Total Orders',
      value: '1,234',
      change: '+15.3%',
      positive: true,
      icon: ShoppingCartIcon,
      color: '#3b82f6',
      bg: '#dbeafe',
    },
    {
      label: 'Customers',
      value: '3,456',
      change: '+12.5%',
      positive: true,
      icon: UserGroupIcon,
      color: '#8b5cf6',
      bg: '#ede9fe',
    },
    {
      label: 'Conversion Rate',
      value: '3.24%',
      change: '-2.4%',
      positive: false,
      icon: ChartBarIcon,
      color: 'var(--primary-600)',
      bg: 'rgba(234, 88, 12, 0.1)',
    },
  ];

  const topProducts = [
    {
      name: 'Premium Wireless Headphones',
      sku: 'WH-1000',
      sales: 2345,
      revenue: 703500,
    },
    {
      name: 'Smart Fitness Watch',
      sku: 'SW-2000',
      sales: 1876,
      revenue: 375200,
    },
    {
      name: 'Mechanical Keyboard RGB',
      sku: 'KB-5000',
      sales: 1543,
      revenue: 231450,
    },
    { name: 'USB-C Hub 7-in-1', sku: 'UH-4000', sales: 1234, revenue: 98720 },
    {
      name: 'Wireless Mouse Ergonomic',
      sku: 'WM-6000',
      sales: 987,
      revenue: 69030,
    },
  ];

  return (
    <AnalyticsContainer>
      <AnalyticsContent>
        <Header>
          <HeaderTop>
            <Title>Analytics</Title>
            <DateRangePicker>
              <option value="7d">Last 7 days</option>
              <option value="30d">Last 30 days</option>
              <option value="90d">Last 90 days</option>
              <option value="12m">Last 12 months</option>
              <option value="ytd">Year to date</option>
            </DateRangePicker>
          </HeaderTop>
        </Header>

        <Main>
          {/* Key Metrics */}
          <MetricsGrid>
            {metrics.map((metric) => (
              <MetricCard key={metric.label}>
                <MetricHeader>
                  <MetricIcon $color={metric.color} $bg={metric.bg}>
                    <metric.icon />
                  </MetricIcon>
                </MetricHeader>
                <MetricLabel>{metric.label}</MetricLabel>
                <MetricValue>{metric.value}</MetricValue>
                <MetricChange $positive={metric.positive}>
                  {metric.positive ? (
                    <ArrowTrendingUpIcon />
                  ) : (
                    <ArrowTrendingDownIcon />
                  )}
                  {metric.change} from last period
                </MetricChange>
              </MetricCard>
            ))}
          </MetricsGrid>

          {/* Charts */}
          <ChartsGrid>
            <ChartCard>
              <ChartHeader>
                <h3>Revenue Overview</h3>
                <p>Daily revenue for the selected period</p>
              </ChartHeader>
              <ChartPlaceholder>
                Chart visualization coming soon
              </ChartPlaceholder>
            </ChartCard>

            <ChartCard>
              <ChartHeader>
                <h3>Order Status</h3>
                <p>Distribution of order statuses</p>
              </ChartHeader>
              <ChartPlaceholder>Pie chart coming soon</ChartPlaceholder>
            </ChartCard>
          </ChartsGrid>

          {/* Top Products */}
          <FullWidthCard>
            <ChartHeader>
              <h3>Top Selling Products</h3>
              <p>Best performing products by revenue</p>
            </ChartHeader>
            <TopProductsTable>
              <thead>
                <tr>
                  <th>Product</th>
                  <th>Sales</th>
                  <th>Revenue</th>
                </tr>
              </thead>
              <tbody>
                {topProducts.map((product) => (
                  <tr key={product.sku}>
                    <td>
                      <ProductName>{product.name}</ProductName>
                      <ProductSKU>SKU: {product.sku}</ProductSKU>
                    </td>
                    <td>
                      <SalesValue>{product.sales.toLocaleString()}</SalesValue>
                      <SalesCount>units sold</SalesCount>
                    </td>
                    <td>
                      <SalesValue>
                        ${(product.revenue / 100).toLocaleString()}
                      </SalesValue>
                    </td>
                  </tr>
                ))}
              </tbody>
            </TopProductsTable>
          </FullWidthCard>
        </Main>
      </AnalyticsContent>
    </AnalyticsContainer>
  );
};

export default Analytics;
